import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Maximize2 } from "lucide-react";
import { fmtCurrency, fmtPct, fmtSignedCompact } from "../../lib/format";

const fmtSeats = (n) => (n == null ? "\u2014" : Math.round(Number(n)).toLocaleString());

function Sparkline({ series, highlight }) {
  const vals = (series || []).map((v) => (v == null ? null : Number(v))).filter((v) => v != null);
  if (vals.length < 2) return null;
  const w = 92, h = 26;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const step = w / (vals.length - 1);
  const coords = vals.map((v, i) => [i * step, h - 3 - ((v - min) / range) * (h - 6)]);
  const line = coords.map((c, i) => `${i ? "L" : "M"}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  const stroke = highlight ? "rgba(255,255,255,0.8)" : "#C5A880";
  const fill = highlight ? "rgba(255,255,255,0.12)" : "rgba(197,168,128,0.16)";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} preserveAspectRatio="none" className="shrink-0" aria-hidden="true">
      <path d={area} fill={fill} stroke="none" />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function Delta({ delta, goodWhenUp, points, highlight }) {
  if (!delta) return null;
  const up = points || delta.pct == null ? (delta.abs ?? 0) > 0 : delta.pct >= 0;
  const isFlat = points || delta.pct == null ? !delta.abs : delta.pct === 0;
  const good = up === goodWhenUp;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const pctLabel = delta.pct == null ? null : `${Math.abs(delta.pct).toFixed(1)}%`;
  const dollars = delta.abs == null ? null : fmtSignedCompact(delta.abs);
  const label = points ? `${(delta.abs * 100).toFixed(1)} pts` : [pctLabel, dollars].filter(Boolean).join(" · ") || "\u2014";
  const color = highlight
    ? (good ? "text-emerald-300" : "text-orange-300")
    : (good ? "text-brand-pos" : "text-brand-neg");
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono whitespace-nowrap shrink-0 ${isFlat ? (highlight ? "text-brand-page/50" : "text-brand-tertiary") : color}`} data-testid="kpi-delta">
      {!isFlat && <Icon className="h-3 w-3" />}
      {label}
    </span>
  );
}

function BudgetGap({ gap, points, highlight, testid }) {
  if (!gap) return null;
  const fav = gap.favorable;
  const color = highlight ? (fav ? "text-emerald-300" : "text-orange-300") : (fav ? "text-brand-pos" : "text-brand-neg");
  const sign = (v) => (v >= 0 ? "+" : "\u2212");
  const label = points
    ? `${sign(gap.abs_pts)}${Math.abs(gap.abs_pts).toFixed(1)} pts`
    : [gap.pct != null ? `${sign(gap.pct)}${Math.abs(gap.pct).toFixed(1)}%` : null, fmtSignedCompact(gap.abs)].filter(Boolean).join(" · ");
  return (
    <p className={`text-[11px] mt-1.5 font-mono whitespace-nowrap ${color}`} data-testid={`${testid}-budget-gap`}>
      {label} <span className={`font-sans ${highlight ? "text-brand-page/50" : "text-brand-tertiary"}`}>vs budget</span>
    </p>
  );
}

function Card({ label, value, sub, highlight, negative, index, testid, delta, goodWhenUp, points, onClick, series, gap }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      data-testid={testid}
      className={`group relative text-left p-6 flex flex-col justify-between min-h-[152px] cursor-pointer transition-colors duration-200 ${highlight ? "bg-brand-ink text-brand-page hover:bg-brand-ink/90" : "bg-brand-surface hover:bg-brand-hover"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className={`text-xs uppercase tracking-[0.15em] ${highlight ? "text-brand-page/60" : "text-brand-tertiary"}`}>
          {label}
        </p>
        <Delta delta={delta} goodWhenUp={goodWhenUp} points={points} highlight={highlight} />
      </div>
      <div className="mt-3">
        <p className={`font-mono text-2xl lg:text-3xl tracking-tight ${highlight ? "text-brand-page" : negative ? "text-brand-neg" : "text-brand-ink"}`}>
          {value}
        </p>
        {sub && (
          <p className={`text-[11px] mt-1 ${highlight ? "text-brand-page/50" : "text-brand-tertiary"}`}>{sub}</p>
        )}
        <BudgetGap gap={gap} points={points} highlight={highlight} testid={testid} />
        {series && <div className="mt-2.5"><Sparkline series={series} highlight={highlight} /></div>}
      </div>
      <div className={`absolute right-4 bottom-4 flex items-center gap-1.5 ${highlight ? "text-brand-page/60" : "text-brand-tertiary"}`}>
        <span className="text-[10px] uppercase tracking-[0.12em] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
          Details
        </span>
        <Maximize2 className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100 transition-opacity duration-200" />
      </div>
    </motion.button>
  );
}

export default function KpiCards({ totals, isRange, comparison, onDrill, trend, budget }) {
  const b = budget?.deltas || {};
  const rangeNote = isRange ? "cumulative" : "";
  const ni = totals.net_income || 0;
  const d = comparison?.deltas || {};
  const occSub = !isRange && totals.total_seats_capacity
    ? `${fmtSeats(totals.total_seats_occupied)} / ${fmtSeats(totals.total_seats_capacity)} seats`
    : "avg. across the period";
  const t = (trend || []).slice(-6);
  const spark = (field) => (t.length >= 2 ? t.map((r) => r[field]) : null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-brand-border border border-brand-border" data-testid="kpi-grid">
      <Card index={0} testid="kpi-net-income" label="Net Income" highlight onClick={() => onDrill("net_income")}
        delta={d.net_income} gap={b.net_income} goodWhenUp series={spark("net_income")}
        value={fmtCurrency(ni)} sub={ni >= 0 ? `Profit after corp allocation ${rangeNote}` : `Loss after corp allocation ${rangeNote}`} />
      <Card index={1} testid="kpi-total-revenue" label="Total Revenue" onClick={() => onDrill("total_revenue")}
        delta={d.total_revenue} gap={b.total_revenue} goodWhenUp series={spark("total_revenue")}
        value={fmtCurrency(totals.total_revenue)} sub={rangeNote || "for the period"} />
      <Card index={2} testid="kpi-total-expenses" label="Total Expenses" onClick={() => onDrill("total_expenses")}
        delta={d.total_expenses} gap={b.total_expenses} goodWhenUp={false} series={spark("total_expenses")}
        value={fmtCurrency(totals.total_expenses)} sub={rangeNote || "for the period"} />
      <Card index={3} testid="kpi-net-ordinary-income" label="Net Ordinary Income" onClick={() => onDrill("net_ordinary_income")}
        delta={d.net_ordinary_income} gap={b.net_ordinary_income} goodWhenUp series={spark("net_ordinary_income")}
        value={fmtCurrency(totals.net_ordinary_income)} sub="Location profit (pre-allocation)" />
      <Card index={4} testid="kpi-occupancy" label="Occupancy" onClick={() => onDrill("occupancy")}
        delta={d.occupancy_pct} gap={budget?.occupancy} goodWhenUp points series={spark("occupancy_pct")}
        value={fmtPct(totals.occupancy_pct)} sub={occSub} />
    </div>
  );
}
